import { useEffect, useState } from "react";
import { Clock, MapPin, Phone, Navigation, Truck } from "lucide-react";
import { Reveal, ChapterTag } from "./Reveal";
import { useReserve } from "../context/ReserveContext";
import { STORE, isStoreOpen } from "../lib/store";

const SHOWROOM_IMG =
    "https://images.unsplash.com/photo-1639405069836-f82aa6dcb900?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHxfHxtb2Rlcm4lMjBzdGFpbmxlc3MlMjBzdGVlbCUyMHJlZnJpZ2VyYXRvciUyMGtpdGNoZW4lMjBsdXh1cnl8ZW58MHx8fHwxNzg5MjcxNzAyfDA&ixlib=rb-4.1.0&q=85";

export default function Showroom() {
    const [open, setOpen] = useState(isStoreOpen());
    const { open: openReserve } = useReserve();

    useEffect(() => {
        const t = setInterval(() => setOpen(isStoreOpen()), 60000);
        return () => clearInterval(t);
    }, []);

    const todayIdx = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", weekday: "short" })
        .format(new Date());
    const todayGroup = STORE.hours.find((h) => h.days.includes({ Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[todayIdx]));

    return (
        <section id="showroom" className="relative py-24 md:py-32 bg-ink2/30 border-t border-white/10">
            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <ChapterTag num="05" title="The Clifton Showroom" testid="showroom-chapter-tag" />
                <Reveal delay={0.05}>
                    <h2 className="mt-8 max-w-3xl font-display font-extrabold tracking-tight text-3xl sm:text-5xl leading-[1.02]">
                        Come kick the tires in <span className="text-gold">Clifton.</span>
                    </h2>
                </Reveal>

                <div className="mt-12 grid lg:grid-cols-2 gap-8 items-stretch">
                    <Reveal>
                        <div className="flex h-full flex-col justify-between gap-8 rounded-2xl border border-white/10 bg-ink p-7 md:p-10">
                            <div className="space-y-7">
                                <div className="flex items-start gap-4">
                                    <MapPin className="mt-1 h-5 w-5 shrink-0 text-gold" />
                                    <div data-testid="clifton-store-address">
                                        <p className="font-display text-xl md:text-2xl font-bold">{STORE.addressLine1}</p>
                                        <p className="font-mono text-sm text-slate-400">{STORE.addressLine2}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <Phone className="mt-1 h-5 w-5 shrink-0 text-gold" />
                                    <a
                                        data-testid="clifton-store-phone"
                                        href={STORE.phoneHref}
                                        className="font-display text-xl md:text-2xl font-bold hover:text-gold transition-colors"
                                    >
                                        {STORE.phoneDisplay}
                                    </a>
                                </div>
                                <div className="flex items-start gap-4">
                                    <Clock className="mt-1 h-5 w-5 shrink-0 text-gold" />
                                    <div data-testid="clifton-store-hours" className="w-full">
                                        {STORE.hours.map((h) => (
                                            <p
                                                key={h.label}
                                                className={`flex justify-between gap-8 border-b border-white/5 py-1.5 font-mono text-xs tracking-wider ${
                                                    h === todayGroup ? "text-gold" : "text-slate-300"
                                                }`}
                                            >
                                                <span>{h.label}</span>
                                                <span>
                                                    {h.open}:00 AM – {h.close - 12}:00 PM
                                                </span>
                                            </p>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-start gap-4" data-testid="store-delivery-info">
                                    <Truck className="mt-1 h-5 w-5 shrink-0 text-gold" />
                                    <div>
                                        <p className="font-display text-xl md:text-2xl font-bold">Delivery available</p>
                                        <p className="font-mono text-sm text-slate-400">
                                            We deliver to Clifton &amp; nearby North Jersey — ask us about your ZIP.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-5">
                                <div className="flex items-center gap-3">
                                    <span className="relative flex h-3 w-3">
                                        <span
                                            className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${
                                                open ? "bg-save" : "bg-hdred"
                                            }`}
                                        />
                                        <span
                                            className={`relative inline-flex h-3 w-3 rounded-full ${
                                                open ? "bg-save" : "bg-hdred"
                                            }`}
                                        />
                                    </span>
                                    <p data-testid="store-open-status" className="font-mono text-xs font-bold uppercase tracking-[0.25em]">
                                        {open ? "Open now — walk in" : "Closed"}
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    <a
                                        href={STORE.mapsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="store-directions-button"
                                        className="flex items-center gap-2 rounded-full bg-gold px-6 py-3.5 font-mono text-[11px] font-bold uppercase tracking-widest text-ink transition-all duration-300 hover:bg-white"
                                    >
                                        <Navigation className="h-3.5 w-3.5" />
                                        Get Directions
                                    </a>
                                    <button
                                        data-testid="reserve-modal-trigger"
                                        onClick={() => openReserve(null)}
                                        className="rounded-full border border-white/20 px-6 py-3.5 font-mono text-[11px] font-bold uppercase tracking-widest text-white transition-all duration-300 hover:border-gold hover:text-gold"
                                    >
                                        Reserve an Appliance
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="relative h-full min-h-[340px] overflow-hidden rounded-2xl border border-white/10">
                            <img
                                src={SHOWROOM_IMG}
                                alt="Showroom kitchen with stainless steel appliances"
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-[1.04]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-7 md:p-10">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">The Showroom</p>
                                <p className="mt-2 font-display text-2xl md:text-3xl font-bold leading-snug">
                                    Full kitchens, staged and ready to test — pulls, doors, drawers and all.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
